import { DateRange } from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";
import { DateTimeUtils } from "@stellar/utils";
import {
  DateRangePreset,
  DateRangeValue,
} from "@components/date-range-picker/date-range-picker-types";

/**
 * @param date optional native Date to convert
 * @returns the luxon DateTime that MUI expects, or null if no date was given
 */
export function toDateTime(date?: Date): DateTime | null {
  return date ? DateTime.fromJSDate(date) : null;
}

/**
 * @param range optional native Date range to convert
 * @returns the luxon range that MUI expects
 */
export function toDateTimeRange(range?: DateRangeValue): DateRange<DateTime> {
  return [toDateTime(range?.[0]), toDateTime(range?.[1])];
}

/**
 * @param range luxon range to convert back
 * @returns the equivalent native Date range
 */
export function toDateRange(range: DateRange<DateTime>): DateRangeValue {
  return [range[0]?.toJSDate(), range[1]?.toJSDate()];
}

/** Shared Intl format for short dates shown in picker controls (e.g. "Jun 9"). */
const SHORT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
};

/**
 * @param range the luxon range to format
 * @returns the start and end of the range as short, human readable strings
 *  (e.g. "Dec 5" / "Dec 14"), or undefined when the range is incomplete
 */
export function formatRangeHeader(
  range: DateRange<DateTime>
): { start: string; end: string } | undefined {
  const [start, end] = range;
  if (!start || !end) {
    return undefined;
  }
  return {
    start: start.toLocaleString(SHORT_DATE_FORMAT),
    end: end.toLocaleString(SHORT_DATE_FORMAT),
  };
}

/**
 * @param range the luxon range to summarize
 * @returns a compact date-range label (e.g. "Jun 9 - Jun 17"),
 *   or undefined when the range is incomplete
 */
export function formatRangeLabel(
  range: DateRange<DateTime>
): string | undefined {
  const [start, end] = range;
  if (!start || !end) {
    return undefined;
  }
  return `${start.toLocaleString(SHORT_DATE_FORMAT)} - ${end.toLocaleString(
    SHORT_DATE_FORMAT
  )}`;
}

/**
 * @param range native date range
 * @returns inclusive time frame length label for analytics tracking (e.g. "45 days")
 */
export function formatRangeLengthInDays(
  range: DateRangeValue
): string | undefined {
  const [startDate, endDate] = range;
  if (!startDate || !endDate) {
    return undefined;
  }

  const numberOfDays =
    Math.abs(DateTimeUtils.diffBetweenTwoDatesInDays(endDate, startDate)) + 1;

  return `${numberOfDays} days`;
}

/**
 * @returns the default set of presets shown in the side bar, matching the
 * FARO design (This week, Last 7 days, Last month, Last 30 days, This Quarter, This year).
 */
export function buildDefaultPresets(): DateRangePreset[] {
  return [
    {
      label: "This week",
      getValue: () => {
        const today = DateTime.now();
        return [
          today.startOf("week").toJSDate(),
          today.endOf("week").toJSDate(),
        ];
      },
    },
    {
      label: "Last 7 days",
      getValue: () => {
        const today = DateTime.now();
        return [
          today.minus({ days: 6 }).startOf("day").toJSDate(),
          today.endOf("day").toJSDate(),
        ];
      },
    },
    {
      label: "Last month",
      getValue: () => {
        const lastMonth = DateTime.now().minus({ months: 1 });
        return [
          lastMonth.startOf("month").toJSDate(),
          lastMonth.endOf("month").toJSDate(),
        ];
      },
    },
    {
      label: "Last 30 days",
      getValue: () => {
        const today = DateTime.now();
        return [
          today.minus({ days: 29 }).startOf("day").toJSDate(),
          today.endOf("day").toJSDate(),
        ];
      },
    },
    {
      label: "This Quarter",
      getValue: () => {
        const today = DateTime.now();
        return [
          today.startOf("quarter").toJSDate(),
          today.endOf("quarter").toJSDate(),
        ];
      },
    },
    {
      label: "This year",
      getValue: () => {
        const today = DateTime.now();
        return [
          today.startOf("year").toJSDate(),
          today.endOf("year").toJSDate(),
        ];
      },
    },
  ];
}

/**
 * @param label preset label text
 * @returns normalized label with first character uppercase and the remainder lowercase
 */
export function formatShortcutLabel(label: string): string {
  const trimmedLabel = label.trim();
  const [first = "", ...rest] = trimmedLabel;
  return `${first.toUpperCase()}${rest.join("").toLowerCase()}`;
}

/**
 * Compares two ranges by day boundaries only, ignoring time-of-day differences.
 * Useful for preset/shortcut active-state matching when values may carry different times.
 */
export function areRangesEqualByDay(
  leftRange: DateRange<DateTime>,
  rightRange: DateRange<DateTime>
): boolean {
  const [leftStart, leftEnd] = leftRange;
  const [rightStart, rightEnd] = rightRange;

  if (!leftStart || !leftEnd || !rightStart || !rightEnd) {
    return false;
  }

  return (
    leftStart.hasSame(rightStart, "day") && leftEnd.hasSame(rightEnd, "day")
  );
}
